/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Row, Table } from '@tanstack/react-table'
import clsx from 'clsx'
import UiTableHeaderLabel from 'components/Ui/UiTableHeaderLabel'
import { UiTableCellTextPrimary, UiTableCellTextSecondary } from 'components/Ui/UiTableCellText'
import UiPackagePill from 'components/Ui/UiPackagePill'
import UiPill from 'components/Ui/UiPill'
import { UiTableColumnVisibilityToggle } from 'components/Ui/UiTableColumnVisibilityToggle'
import UiCompanyTypePill from 'components/Ui/UiCompanyTypePill'
import UiTableRowActionsContainer from 'components/Ui/UiTableRowActionsContainer'
import UiTableRowActionButton from 'components/Ui/UiTableRowActionButton'
import { UI_TABLE_ACTIONS_CELL_CLASS } from 'components/Ui/UiTableSharedStyles'
import UiInlineSelect from 'components/Ui/UiInlineSelect'
import UiInlineOfferStatusSelect from 'components/Offers/UiInlineOfferStatusSelect'
import { Offer } from 'types/entities/Offer'
import { Driver } from 'types/entities/Driver'
import { PackageCategory } from 'types/enums/PackageCategory'
import Dates from 'utils/Dates'
import { getShortNameFromId } from 'utils/reservationUtils'
import { getPackageCategoryClassName } from 'utils/packageUtils'
import { currencyNumberFormat, numberRangeColumnFilter } from 'utils/utility'
import { driverName } from 'utils/driverUtils'
import { getContactGenderLabel, useGetBusinessCategories } from 'utils/companyUtils'
import { getPrimaryCompanyAddress } from 'utils/companiesUtils'
import { tableActionsMeta } from 'utils/table/tableColumnWidths'
import { exportColumn, exportField } from 'utils/table/tableColumnExport'
import type { OffersTableRow } from 'utils/offers/offersUtils'
import { formatAddressSummary } from 'utils/addressUtils'
import { useAppDispatch, AppDispatch } from 'helpers/store'
import { OffersAction } from 'store/offers/OffersAction'
import { useSellers } from 'utils/sellerUtils'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import { createAssignmentReminderForEntity } from 'utils/reminders/createAssignmentReminder'

export interface UseOffersTableFlatColumnsOptions {
    sellerById: (sellerId: number) => Driver | null | undefined
    onEdit: (offer: Offer) => void
    onDelete: (offer: Offer) => void
    onDuplicate: (offer: Offer) => void
}

export const useOffersTableFlatColumns = ({
    sellerById,
    onEdit,
    onDelete,
    onDuplicate,
}: UseOffersTableFlatColumnsOptions) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { findByBusinessCategoryId } = useGetBusinessCategories()
    const { selectOptions: sellerOptions } = useSellers()

    const columns = useMemo(
        () => [
            {
                accessorFn: (row: OffersTableRow) =>
                    row.rowType === 'calculation' ? row.calculation?.id ?? 0 : row.offer_id,
                id: 'id',
                meta: exportColumn(exportField('id', 'ID')),
                enableSorting: true,
                enableColumnFilter: true,
                header: () => <UiTableHeaderLabel>{t('ID')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    const currentRow = row.original
                    if (currentRow.rowType === 'calculation') {
                        return (
                            <UiTableCellTextSecondary className="flex items-center gap-2 pl-4" size="xs">
                                <span className="text-sky-500">↳</span>
                                <span>{currentRow.calculation?.id ?? '-'}</span>
                            </UiTableCellTextSecondary>
                        )
                    }
                    return <UiTableCellTextPrimary>{currentRow.offer_id}</UiTableCellTextPrimary>
                },
            },
            {
                accessorFn: (row: OffersTableRow) => {
                    const seller = row.offer?.seller ?? sellerById(row.seller_id)
                    return seller ? driverName(seller, true) : '-'
                },
                id: 'seller_id',
                meta: exportColumn(exportField('seller.name', 'Seller')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Seller')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    const offer = row.original.offer
                    return (
                        <UiInlineSelect
                            ariaLabel={t('Seller') as string}
                            value={offer.seller_id}
                            options={sellerOptions.map((option) => ({
                                value: option.value,
                                label: option.label,
                            }))}
                            onSave={async (sellerId) => {
                                const nextSellerId = Number(sellerId)
                                await dispatch(
                                    OffersAction.updateOffer({
                                        id: offer.id,
                                        seller_id: nextSellerId,
                                    })
                                )
                                await createAssignmentReminderForEntity(
                                    dispatch as unknown as AppDispatch,
                                    {
                                        modelName: ReminderModelName.Offer,
                                        modelId: Number(offer.id),
                                        sellerId: nextSellerId,
                                        previousSellerId: offer.seller_id,
                                        hallId: (offer as { hall_id?: number }).hall_id,
                                        content: t('Assigned to you') as string,
                                    }
                                )
                            }}
                        />
                    )
                },
            },
            {
                accessorFn: (row: OffersTableRow) => row.offer_number ?? null,
                id: 'offer_number',
                meta: exportColumn(exportField('number', 'Offer #')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Offer number')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.offer_number ?? '---'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'created_at',
                meta: exportColumn(exportField('created_at', 'Created')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Created')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => (
                    <UiTableCellTextSecondary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {Dates.date(row.original.created_at) || '-'}
                    </UiTableCellTextSecondary>
                ),
            },
            {
                accessorKey: 'updated_at',
                meta: exportColumn(exportField('updated_at', 'Updated')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Updated')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => (
                    <UiTableCellTextSecondary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {Dates.date(row.original.updated_at) || '-'}
                    </UiTableCellTextSecondary>
                ),
            },
            {
                accessorKey: 'company_name',
                meta: exportColumn(exportField('company.name', 'Company')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Company')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    const currentRow = row.original
                    if (currentRow.rowType === 'calculation') {
                        return (
                            <div className="flex items-center gap-2 pl-4">
                                <UiTableCellTextSecondary size="xs">
                                    {currentRow.calculation_label}
                                </UiTableCellTextSecondary>
                                {currentRow.is_target && (
                                    <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                        {t('Target')}
                                    </span>
                                )}
                            </div>
                        )
                    }
                    return <UiTableCellTextPrimary className="break-words">{currentRow.company_name || '-'}</UiTableCellTextPrimary>
                },
            },
            {
                accessorFn: (row: OffersTableRow) => row.company_type ?? null,
                id: 'company.type',
                meta: exportColumn(exportField('company.type', 'Account Type')),
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Type')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    if (!row.original.company_type) {
                        return <UiTableCellTextPrimary>-</UiTableCellTextPrimary>
                    }
                    return <UiCompanyTypePill type={row.original.company_type} />
                },
            },
            {
                accessorKey: 'calculations_count',
                meta: exportColumn(exportField('calculations_count', 'Calcs')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Calcs')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return (
                        <span
                            className={clsx(
                                'inline-flex min-w-8 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold',
                                row.original.calculations_count > 1
                                    ? 'bg-sky-100 text-sky-700'
                                    : 'bg-gray-100 text-gray-700'
                            )}
                        >
                            {row.original.calculations_count}
                        </span>
                    )
                },
            },
            {
                accessorFn: (row: OffersTableRow) =>
                    getShortNameFromId(row.package ?? undefined) ?? row.package ?? null,
                id: 'package',
                meta: exportColumn(exportField('packagesCart.category', 'Ticket type')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Ticket type')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    const packageName = (row.original.package ?? undefined) as PackageCategory | undefined
                    return (
                        <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                            <UiPackagePill
                                className={getPackageCategoryClassName(packageName)}
                                value={getShortNameFromId(packageName)}
                            />
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'duration_breakdown',
                meta: exportColumn(exportField('duration_breakdown', 'Stay')),
                enableSorting: false,
                enableColumnFilter: false,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Stay')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        <span className={'inline-flex items-center gap-1.5'}>
                            {row.original.duration_karting_hours > 0 && (
                                <UiPill className={'bg-red-100 text-red-700'}>
                                    {row.original.duration_karting_hours}h
                                </UiPill>
                            )}
                            {row.original.duration_non_karting_hours > 0 && (
                                <UiPill className={'bg-yellow-100 text-yellow-800'}>
                                    {row.original.duration_non_karting_hours}h
                                </UiPill>
                            )}
                            {row.original.duration_karting_hours <= 0 && row.original.duration_non_karting_hours <= 0 && (
                                <UiPill className={'bg-gray-100 text-gray-700'}>{row.original.duration_breakdown || '-'}</UiPill>
                            )}
                        </span>
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'date',
                meta: exportColumn(exportField('packagesCart.date', 'Event date')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Event date')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.date ? Dates.date(row.original.date) : '-'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'event_time',
                meta: exportColumn(
                    exportField('packagesCart.hour', 'Event time'),
                    exportField('packagesCart.minute', 'Event time')
                ),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Event time')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.event_time || '-'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'persons',
                meta: exportColumn(
                    exportField('packagesCart.drivers_count_adult', 'Persons'),
                    exportField('packagesCart.drivers_count_child', 'Persons')
                ),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                filterFn: numberRangeColumnFilter,
                header: () => <UiTableHeaderLabel>{t('Persons')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {row.original.persons > 0 ? row.original.persons : '-'}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'status',
                meta: exportColumn(exportField('status', 'Offer Status')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Offer Status')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return <UiInlineOfferStatusSelect offer={row.original.offer} />
                },
            },
            {
                accessorKey: 'has_reservation',
                meta: exportColumn(exportField('has_reservation', 'Has reservation')),
                enableSorting: true,
                enableColumnFilter: false,
                enableGlobalFilter: true,
                header: () => <UiTableHeaderLabel>{t('Has reservation')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }

                    const hasReservation = row.original.has_reservation

                    return (
                        <UiTableCellTextPrimary>
                            <UiPill
                                className={
                                    hasReservation
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-gray-100 text-gray-700'
                                }
                            >
                                {hasReservation ? t('Yes') : t('No')}
                            </UiPill>
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'value',
                meta: exportColumn(exportField('packagesCart.price', 'Value')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: true,
                filterFn: numberRangeColumnFilter,
                header: () => <UiTableHeaderLabel>{t('Revenue')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => (
                    <UiTableCellTextPrimary className={row.original.rowType === 'calculation' ? 'pl-4' : undefined}>
                        {currencyNumberFormat(row.original.value ?? 0)}
                    </UiTableCellTextPrimary>
                ),
            },
            {
                accessorKey: 'valid_until',
                meta: exportColumn(exportField('valid_until', 'Valid until')),
                enableSorting: true,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Valid until')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return (
                        <UiTableCellTextPrimary>
                            {row.original.valid_until ? Dates.date(row.original.valid_until) : '-'}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                id: 'company_branche',
                meta: exportColumn(exportField('company.business_category', 'Branche')),
                accessorFn: (row: OffersTableRow) => row.offer?.company?.business_category || '',
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Branche')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    const branchValue = findByBusinessCategoryId(
                        row.original.offer?.company?.business_category ?? ''
                    )
                    return <UiTableCellTextPrimary>{branchValue?.name || '-'}</UiTableCellTextPrimary>
                },
            },
            {
                id: 'company_state',
                meta: exportColumn(exportField('company.address.region', 'Kanton')),
                accessorFn: (row: OffersTableRow) => {
                    const company = row.offer?.company
                    return company ? getPrimaryCompanyAddress(company)?.region ?? '' : ''
                },
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Kanton')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    const company = row.original.offer?.company
                    const region = company ? getPrimaryCompanyAddress(company)?.region : undefined
                    return <UiTableCellTextPrimary>{region || '-'}</UiTableCellTextPrimary>
                },
            },
            {
                id: 'company_address',
                meta: exportColumn(
                    exportField('address.street_name', 'Address'),
                    exportField('address.postal_code', 'Postal code'),
                    exportField('address.town', 'Town'),
                    exportField('address.country', 'Country')
                ),
                accessorFn: (row: OffersTableRow) => {
                    const address = row.offer?.company ? getPrimaryCompanyAddress(row.offer.company) : undefined
                    return formatAddressSummary(address?.street_name, address?.postal_code, address?.town)
                },
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Address')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    const address = row.original.offer?.company
                        ? getPrimaryCompanyAddress(row.original.offer.company)
                        : undefined
                    return (
                        <div className="flex flex-col">
                            {address?.street_name && (
                                <UiTableCellTextPrimary>{address.street_name}</UiTableCellTextPrimary>
                            )}
                            {(address?.postal_code || address?.town) && (
                                <UiTableCellTextSecondary>
                                    {[address.postal_code, address.town].filter(Boolean).join(' ')}
                                </UiTableCellTextSecondary>
                            )}
                        </div>
                    )
                },
            },
            {
                id: 'contact_position',
                meta: exportColumn(exportField('contact.position', 'Position')),
                accessorFn: (row: OffersTableRow) => row.offer?.contact?.position || '',
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Position')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return <UiTableCellTextPrimary>{row.original.offer?.contact?.position || '-'}</UiTableCellTextPrimary>
                },
            },
            {
                id: 'contact_gender',
                meta: exportColumn(exportField('contact.gender', 'Geschlecht')),
                accessorFn: (row: OffersTableRow) => row.offer?.contact?.gender || '',
                enableSorting: false,
                enableColumnFilter: true,
                enableGlobalFilter: false,
                header: () => <UiTableHeaderLabel>{t('Geschlecht')}</UiTableHeaderLabel>,
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return <UiTableCellTextSecondary size="xs">-</UiTableCellTextSecondary>
                    }
                    return (
                        <UiTableCellTextPrimary>
                            {getContactGenderLabel(row.original.offer?.contact?.gender, t)}
                        </UiTableCellTextPrimary>
                    )
                },
            },
            {
                accessorKey: 'actions',
                ...tableActionsMeta(3),
                enableSorting: false,
                enableColumnFilter: false,
                header: ({ table }: { table: Table<OffersTableRow> }) => (
                    <div className={clsx('flex flex-row items-center leading-tight justify-end h-full')}>
                        <UiTableColumnVisibilityToggle table={table} />
                    </div>
                ),
                cell: ({ row }: { row: Row<OffersTableRow> }) => {
                    if (row.original.rowType === 'calculation') {
                        return null
                    }
                    const offer = row.original.offer
                    if (!offer) return null
                    return (
                        <UiTableRowActionsContainer className={UI_TABLE_ACTIONS_CELL_CLASS}>
                            <UiTableRowActionButton
                                variant={'edit'}
                                title={t('Edit')}
                                onClick={() => onEdit(offer)}
                            />
                            <UiTableRowActionButton
                                variant={'duplicate'}
                                title={t('Duplicate')}
                                onClick={() => onDuplicate(offer)}
                            />
                            <UiTableRowActionButton
                                variant={'delete'}
                                title={t('Delete')}
                                onClick={() => onDelete(offer)}
                            />
                        </UiTableRowActionsContainer>
                    )
                },
            },
        ],
        [t, sellerById, sellerOptions, dispatch, onEdit, onDelete, onDuplicate, findByBusinessCategoryId]
    )

    return { columns }
}
